import { clsx } from 'clsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { useEffect, useState } from 'react';
import { ExpandCollapseIcon } from 'src/pages/ProjectPage/CreateProjectSteps/ExpandCollapseIcon/ExpandCollapseIcon';
import { CreateNewKBInput } from 'src/components/KnowledgeBase/CreateNewKBInput/CreateNewKBInput';
import { useParams } from 'react-router-dom';
import {
  useAssignProjectKnowledgeBaseMutation,
  useCreateKnowledgeBaseMutation,
  useGetProjectByIdQuery,
} from 'src/services';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { Skeleton } from 'src/components/ui/skeleton';
import { ProductsTypeBack } from 'src/constants/products';
import { CreateNewKBDataSources } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CreateNewKBAccordion/CreanteNewKBDataSources';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import { useAppDispatch } from 'src/store';
import type { KnowledgeBase, KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';

const LoadingSkeleton = () => {
  return (
    <div className={'flex flex-col gap-5 min-w-[455px]'}>
      <Skeleton className={'w-full h-4'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-4'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
      <Skeleton className={'w-full h-10'} />
    </div>
  );
};

export const CreateNewKBAccordion = ({
  item,
  onCollectorsChanged = () => {},
}: {
  item?: KnowledgeBase;
  onCollectorsChanged?: (collectors: KnowledgeBaseCollector[]) => void;
}) => {
  const [value, setValue] = useState('');
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [disabled] = useState(false);
  const [isKBCreated, setIsKBCreated] = useState(false);
  const { id, step } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery(
    { id: id },
    { refetchOnMountOrArgChange: true },
  );
  const dispatch = useAppDispatch();
  const [createKnowledgeBase, { data, isLoading, isSuccess }] = useCreateKnowledgeBaseMutation();
  const [assignKB] = useAssignProjectKnowledgeBaseMutation();
  const isExpanded = value === 'new-kb';

  useEffect(() => {
    item && setKnowledgeBase(item);
    item && !knowledgeBase && setValue('new-kb');

    if (isExpanded && !item && !isKBCreated) {
      createKnowledgeBase({ name: `Knowledge Base for ${currentProject?.name || ''}` });
    }
  }, [item, isExpanded]);

  useEffect(() => {
    if (isSuccess && data) {
      setIsKBCreated(true);
      setKnowledgeBase(data);

      assignKB({
        project_type: ProductsTypeBack[currentProject?.project_type],
        projectId: currentProject.id,
        body: [data.id],
      });

      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          formData: null,
          knowledgeBaseId: data.id,
        }),
      );
    }
  }, [isSuccess, data]);

  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-center gap-5',
        !isExpanded && 'w-[455px]',
      )}
    >
      <Accordion
        value={value}
        onValueChange={setValue}
        type="single"
        className="w-full"
        collapsible={true}
        disabled={disabled}
      >
        <AccordionItem
          value="new-kb"
          className={clsx(
            'group/main-accordion-item rounded-sm-5 border-0 transition-shadow shadow-none hover:!shadow-none',
            !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
            disabled && 'opacity-70',
          )}
        >
          <AccordionTrigger
            hideDefaultIcon
            className="py-2.5 px-0 group/trigger w-full flex flex-row items-start gap-5"
          >
            <div className={clsx((isLoading || !knowledgeBase || !isExpanded) && 'hidden')}>
              <CheckIcon className={'invisible w-[27px]'} />
            </div>
            <div className="flex items-center justify-between w-full">
              <div
                className={
                  'flex flex-row gap-[7px] text-violet font-semibold text-sm group-hover/trigger:underline'
                }
              >
                <p>Create new knowledge base</p>
              </div>
              <ExpandCollapseIcon expanded={isExpanded} />
            </div>
            <div
              className={clsx(
                'invisible w-[27px]',
                (isLoading || !knowledgeBase || !isExpanded) && 'hidden',
              )}
            >
              <AddItemIconDisabled />
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={clsx('px-0 pb-0 mt-4', (!knowledgeBase || !isExpanded) && 'opacity-0')}
          >
            {!knowledgeBase || isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className={'flex flex-col gap-1.5'}>
                <CreateNewKBInput item={knowledgeBase} />
                {knowledgeBase && (
                  <CreateNewKBDataSources
                    item={knowledgeBase}
                    onCollectorsChanged={onCollectorsChanged}
                  />
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
